// Carry Roll Diagnostics — strike, dispersion, shortfall, fitting patterns (9.1–9.5). Pure functions.
/** Strike-quality / mishit carry modifier (9.1). strikeQuality 0..1 (1 = perfect). */
export function strikeModifier(result, strikeQuality) {
    const q = Math.max(0, Math.min(1, strikeQuality));
    const modifier = 0.85 + q * 0.15; // 0.85 worst .. 1.0 perfect
    const adjustedCarryM = result.carryM * modifier;
    return {
        modifier,
        adjustedCarryM,
        message: `Strike quality ${(q * 100).toFixed(0)}%: carry ${Math.round(result.carryM)} → ${Math.round(adjustedCarryM)} m.`,
    };
}
/** Handicap-scaled dispersion modifier (9.2). Returns dispersion in metres. */
export function handicapDispersion(handicap) {
    // Scratch ~ 8 m lateral dispersion; each handicap stroke adds ~0.8 m
    return 8 + Math.max(0, handicap) * 0.8;
}
/** Attribute shortfall when actual carry diverges from expected (9.3). */
export function attributeShortfall(expectedCarryM, actualCarryM, request) {
    const diff = actualCarryM - expectedCarryM;
    const causes = [];
    if (Math.abs(diff) < 3)
        return causes;
    const env = request.environment;
    // Heuristic attribution: rank candidate causes by plausible magnitude
    const candidates = [
        { factor: 'ball speed', delta: diff * 0.5, message: `Ball speed accounts for ~${Math.round(Math.abs(diff) * 0.5)} m of the gap.` },
        { factor: 'launch angle', delta: diff * 0.2, message: `Launch angle accounts for ~${Math.round(Math.abs(diff) * 0.2)} m.` },
        { factor: 'spin', delta: diff * 0.15, message: `Spin rate accounts for ~${Math.round(Math.abs(diff) * 0.15)} m.` },
        { factor: 'environment', delta: diff * 0.15, message: `Conditions (wind/altitude at ${Math.round(env.altitudeM)} m) account for ~${Math.round(Math.abs(diff) * 0.15)} m.` },
    ];
    // Sort by magnitude
    candidates.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    causes.push(...candidates.slice(0, 3));
    return causes;
}
/** Detect equipment-fitting patterns (9.5). */
export function detectFittingPatterns(session) {
    const signals = [];
    if (session.results.length < 3)
        return signals;
    const carries = session.results.map((r) => r.carryM);
    const avg = carries.reduce((a, b) => a + b, 0) / carries.length;
    const variance = carries.reduce((a, b) => a + (b - avg) ** 2, 0) / carries.length;
    const stddev = Math.sqrt(variance);
    if (stddev > 12) {
        signals.push({
            type: 'consistency',
            message: `Carry varies by ${Math.round(stddev)} m — inconsistent contact; consider a more forgiving club.`,
        });
    }
    const avgApex = session.results.reduce((a, r) => a + r.apexM, 0) / session.results.length;
    if (avgApex > 32) {
        signals.push({
            type: 'trajectory',
            message: 'High average apex — consider a lower-spinning shaft or less loft.',
        });
    }
    return signals;
}
export function optimizationTriangle(request) {
    const l = request.launch;
    // Balance score: how close the combo is to "optimal" for the ball speed.
    const optimalSpin = l.ballSpeed * 35;
    const optimalLaunch = 12 + (75 - l.ballSpeed) * 0.1;
    const spinScore = 1 - Math.min(1, Math.abs(l.spinRpm - optimalSpin) / 2000);
    const launchScore = 1 - Math.min(1, Math.abs((l.launchAngle * 180) / Math.PI - optimalLaunch) / 5);
    return {
        ballSpeed: l.ballSpeed,
        launchAngle: (l.launchAngle * 180) / Math.PI,
        spin: l.spinRpm,
        balance: (spinScore + launchScore) / 2,
    };
}
//# sourceMappingURL=diagnostics.js.map